import { AbiCoder, Interface, MaxUint256, Signature } from 'ethers';
import { Contract, Provider, Signer, utils } from 'zksync-ethers';

import {
  REACT_APP_OPENZK_RPC,
  REACT_APP_PERMIT_PAYMASTER_ADDRESS,
} from '../../../Config/Config';

const PAYMASTER_ABI = [
  'function getCharge(uint256 gasLimit, uint256 maxFeePerGas) view returns (uint256)',
  'function ozk() view returns (address)',
];

const ERC20_ABI = [
  'function balanceOf(address) view returns (uint256)',
  'function name() view returns (string)',
  'function version() view returns (string)',
  'function nonces(address) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function transfer(address to, uint256 amount) returns (bool)',
];

const PAYMASTER_GAS_OVERHEAD = BigInt(
  process.env.REACT_APP_PAYMASTER_GAS_OVERHEAD || '500000',
);
const ZERO = BigInt(0);
const FALLBACK_INNER_GAS = BigInt(300000);

const erc20Interface = new Interface(ERC20_ABI);

async function signPermitInnerInput(
  signer: Signer,
  paymasterAddress: string,
  token: string,
  permitValue: bigint,
): Promise<string> {
  const ozk = new Contract(token, ERC20_ABI, signer);
  const network = await signer.provider.getNetwork();
  let version = '1';
  try {
    version = await ozk.version();
  } catch {
    // OpenZeppelin ERC20Permit defaults to "1".
  }

  const owner = await signer.getAddress();
  const [name, nonce] = await Promise.all([ozk.name(), ozk.nonces(owner)]);
  const deadline = MaxUint256;
  const sigHex = await signer.signTypedData(
    {
      name,
      version,
      chainId: network.chainId,
      verifyingContract: token,
    },
    {
      Permit: [
        { name: 'owner', type: 'address' },
        { name: 'spender', type: 'address' },
        { name: 'value', type: 'uint256' },
        { name: 'nonce', type: 'uint256' },
        { name: 'deadline', type: 'uint256' },
      ],
    },
    {
      owner,
      spender: paymasterAddress,
      value: permitValue,
      nonce,
      deadline,
    },
  );
  const { v, r, s } = Signature.from(sigHex);
  return AbiCoder.defaultAbiCoder().encode(
    ['uint256', 'uint256', 'uint256', 'uint8', 'bytes32', 'bytes32'],
    [permitValue, permitValue, deadline, v, r, s],
  );
}

async function sendStandardErc20Transfer({
  signer,
  tokenAddress,
  data,
}: {
  signer: Signer;
  tokenAddress: string;
  data: string;
}) {
  return signer.sendTransaction({
    to: tokenAddress,
    value: ZERO,
    data,
  });
}

export async function sendErc20WithOzkPermit({
  signer,
  tokenAddress,
  recipient,
  amount,
}: {
  signer: Signer;
  tokenAddress: string;
  recipient: string;
  amount: bigint;
}) {
  const provider = new Provider(REACT_APP_OPENZK_RPC);
  const from = await signer.getAddress();
  const data = erc20Interface.encodeFunctionData('transfer', [
    recipient,
    amount,
  ]);

  const paymasterAddress = REACT_APP_PERMIT_PAYMASTER_ADDRESS;
  if (!paymasterAddress) {
    return sendStandardErc20Transfer({ signer, tokenAddress, data });
  }

  const paymaster = new Contract(paymasterAddress, PAYMASTER_ABI, provider);
  const ozkAddress: string = await paymaster.ozk();
  const ozk = new Contract(ozkAddress, ERC20_ABI, provider);
  const ozkBal: bigint = await ozk.balanceOf(from);

  if (ozkBal <= ZERO) {
    return sendStandardErc20Transfer({ signer, tokenAddress, data });
  }

  const txLike = {
    from,
    to: tokenAddress,
    value: ZERO,
    data,
  };

  let innerGas: bigint;
  try {
    innerGas = BigInt((await provider.estimateGas(txLike)).toString());
  } catch {
    innerGas = FALLBACK_INNER_GAS;
  }

  const gasLimit = innerGas + PAYMASTER_GAS_OVERHEAD;
  const gasPrice = BigInt((await provider.getGasPrice()).toString());
  const charge: bigint = await paymaster.getCharge(gasLimit, gasPrice);

  const transferringOzk =
    tokenAddress.toLowerCase() === ozkAddress.toLowerCase();
  const hasFeeCover =
    ozkBal >= charge && (!transferringOzk || ozkBal >= amount + charge);

  if (!hasFeeCover) {
    return sendStandardErc20Transfer({ signer, tokenAddress, data });
  }

  const innerInput = await signPermitInnerInput(
    signer,
    paymasterAddress,
    ozkAddress,
    charge,
  );
  const paymasterParams = utils.getPaymasterParams(paymasterAddress, {
    type: 'General',
    innerInput,
  });

  return signer.sendTransaction({
    to: tokenAddress,
    value: ZERO,
    data,
    gasLimit,
    maxFeePerGas: gasPrice,
    maxPriorityFeePerGas: gasPrice,
    customData: {
      gasPerPubdata: utils.DEFAULT_GAS_PER_PUBDATA_LIMIT,
      paymasterParams,
    },
  });
}
